import type * as ngRouter from '@angular/router'
import { isDeepEqual } from '@attain-apps/data-utils'

import { EncodedQueryParam } from './encoded-query-param'

/**
 * Represents a navigation request that can modify the router state. E.g., by
 * changing the path or by updating query parameters
 */
export interface Request extends ngRouter.NavigationBehaviorOptions {
  /**
   * Finalized URL to navigate to. If this is passed, all other properties are ignored
   */
  url?: string | ngRouter.UrlTree
  /**
   * Path to be navigated to
   *
   * @example
   * [ '/rate', 'rules' ]
   */
  path?: readonly string[]
  /**
   * Query parameters to be merged with existing query parameters
   */
  queryParams?: {
    /**
     * @see {@link EncodedQueryParam}
     */
    encoded?: readonly EncodedQueryParam[]
    /**
     * Simple, unaltered query params
     *
     * Set a query param to `null` to remove it
     */
    plain?: Record<string, string | null>
  }
  /**
   * Fragment to be navigated to. This is the part after the `#`
   */
  fragment?: string
}
export namespace Request {
  /**
   * Creates a final lookup of query params from the given {@link Request} and
   * {@link EncodedQueryParam.FullObject}
   */
  export function synthesizeQueryParams(
    request: Request,
    currentEncodedQueryParamsFullObject:
      | EncodedQueryParam.FullObject
      | undefined,
  ): Record<string, string | null> {
    let encodedQueryParamsFullObject = currentEncodedQueryParamsFullObject
    if (currentEncodedQueryParamsFullObject) {
      for (const encodedQueryParam of request.queryParams?.encoded ?? []) {
        encodedQueryParamsFullObject = EncodedQueryParam.apply(
          encodedQueryParamsFullObject ?? {},
          encodedQueryParam,
        )
      }
      // If final query params are unchanged, set to undefined to
      // avoid redundant navigation
      if (
        isDeepEqual(
          encodedQueryParamsFullObject,
          currentEncodedQueryParamsFullObject,
        )
      ) {
        encodedQueryParamsFullObject = undefined
      }
    }
    return {
      ...request.queryParams?.plain,
      // NOTE: if the `currentEncodedQueryParamsFullObject` is undefined, that
      // means nothing has changed (per the logic above), so we don't want to
      // modify it
      ...(encodedQueryParamsFullObject
        ? {
            [EncodedQueryParam.KEY]:
              // NOTE: if the result of the encoding is `undefined`, that means
              // it is now empty and we should get rid of it (done by setting to
              // `null`)
              EncodedQueryParam.FullObject.encode(
                encodedQueryParamsFullObject,
              ) ?? null,
          }
        : {}),
    }
  }
}
